// Lightweight Firebase wrapper for QuickDukaan phone authentication.
// Single Firebase app instance + small helpers for invisible reCAPTCHA + SMS OTP.
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Ensure we don't double-attach a RecaptchaVerifier to the same DOM node —
// React StrictMode / hot reload otherwise blows it up.
let _recaptcha = null;
let _recaptchaEl = null; // the throwaway <div> we rendered into

function _createThrowawayContainer() {
  // Create a fresh, uniquely-id'd hidden <div> for each verifier so we never
  // collide with a previous reCAPTCHA widget left behind by Google's global
  // grecaptcha registry. This is the only reliable way to avoid
  // "reCAPTCHA has already been rendered in this element".
  const el = document.createElement("div");
  el.id = `qd-recaptcha-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  el.style.position = "fixed";
  el.style.bottom = "0";
  el.style.left = "0";
  el.style.pointerEvents = "none";
  document.body.appendChild(el);
  return el;
}

function _destroyVerifier() {
  try { _recaptcha?.clear?.(); } catch { /* ignore */ }
  if (_recaptchaEl) {
    try { _recaptchaEl.parentNode?.removeChild(_recaptchaEl); } catch { /* ignore */ }
  }
  _recaptcha = null;
  _recaptchaEl = null;
}

// `_unusedContainerId` is kept only for backward-compat with existing call
// sites — we no longer render into the caller's div because Firebase pins the
// grecaptcha widget to the element forever, which prevents a second attempt.
export function getRecaptcha(_unusedContainerId) {
  // Always create a fresh verifier on a brand-new throwaway element.
  _destroyVerifier();
  _recaptchaEl = _createThrowawayContainer();

  _recaptcha = new RecaptchaVerifier(auth, _recaptchaEl, {
    size: "invisible",
    callback: () => {},
    "expired-callback": () => { _destroyVerifier(); },
  });
  return _recaptcha;
}

export function resetRecaptcha() {
  _destroyVerifier();
}

// ───────────────────────── Network diagnostics ─────────────────────────
// Best-effort reachability checks for Firebase / Identity Toolkit so we can
// distinguish "Firebase is down on this network" from genuine auth errors.
async function _probe(url, label) {
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 6000);
    // We use no-cors GET — we only care that the request didn't fail at the
    // network/DNS/TLS layer. CORS/4xx responses still count as "reachable".
    await fetch(url, { method: "GET", mode: "no-cors", signal: ctrl.signal });
    clearTimeout(tid);
    return { label, url, ok: true };
  } catch (e) {
    return { label, url, ok: false, error: e?.message || String(e) };
  }
}

// CORS-enabled probe that actually reads the HTTP status — this is what
// reveals API-key referrer restrictions (HTTP 403 / 400 with PERMISSION_DENIED)
// that the no-cors probe cannot see.
async function _corsProbe(url, label) {
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(url, { method: "GET", signal: ctrl.signal });
    clearTimeout(tid);
    let body = null;
    try { body = await res.json(); } catch { /* ignore */ }
    return { label, url, status: res.status, ok: res.ok, body };
  } catch (e) {
    return { label, url, ok: false, error: e?.message || String(e) };
  }
}

// Diagnostic version — bump this so the user can confirm they are running
// the latest JS (rather than a cached old build on mobile Chrome).
const DIAG_VERSION = "v3-2026-02";

export async function runFirebaseDiagnostics() {
  /* eslint-disable no-console */
  console.group(`Firebase diagnostics ${DIAG_VERSION}`);
  console.log("hostname:", window.location.hostname);
  console.log("authDomain:", firebaseConfig.authDomain);
  console.log("projectId:", firebaseConfig.projectId);
  console.log("apiKey set:", !!firebaseConfig.apiKey);
  console.log("appId set:", !!firebaseConfig.appId);

  // 1. Reachability — Firebase auth endpoints
  const reach = await Promise.all([
    _probe("https://identitytoolkit.googleapis.com/", "identitytoolkit root"),
    _probe(`https://${firebaseConfig.authDomain}/`, "authDomain"),
    // 2. reCAPTCHA endpoints — Phone Auth REQUIRES these to load
    _probe("https://www.google.com/recaptcha/api.js", "recaptcha api.js"),
    _probe("https://www.gstatic.com/recaptcha/releases/", "gstatic recaptcha"),
    _probe("https://www.google.com/recaptcha/api2/anchor", "recaptcha anchor"),
  ]);

  // 3. CORS probe — Identity Toolkit accepts the API key from this origin
  const cors = await _corsProbe(
    `https://identitytoolkit.googleapis.com/v1/projects?key=${encodeURIComponent(firebaseConfig.apiKey)}`,
    "identitytoolkit projects (CORS)",
  );

  reach.forEach((p) => console.log(p.ok ? "✓" : "✗", p.label, p));
  console.log(cors.ok ? "✓" : "✗", cors.label, cors);
  console.groupEnd();
  /* eslint-enable no-console */

  const firebaseReachable = reach[0].ok && reach[1].ok;
  const recaptchaReachable = reach[2].ok && reach[3].ok && reach[4].ok;

  // Build a precise diagnosis.
  let diagnosis = `[${DIAG_VERSION}] `;
  if (!firebaseReachable) {
    diagnosis +=
      "Firebase auth endpoints are NOT reachable from this browser.\n\n" +
      reach.slice(0, 2).filter((p) => !p.ok).map((p) => `• ${p.label} → ${p.error || "blocked"}`).join("\n");
  } else if (!recaptchaReachable) {
    diagnosis +=
      "reCAPTCHA endpoints are BLOCKED on this network — Phone OTP cannot work " +
      "without reCAPTCHA. Common cause: ISP/DNS/VPN/ad-blocker blocking " +
      "google.com/recaptcha or gstatic.com.\n\n" +
      "Blocked:\n" +
      reach.slice(2).filter((p) => !p.ok).map((p) => `• ${p.label} → ${p.error || "blocked"}`).join("\n") +
      "\n\nFixes to try:\n" +
      "1) Switch from mobile data → Wi-Fi (or vice versa)\n" +
      "2) Disable any ad-blocker / Brave Shields / 1.1.1.1 / VPN\n" +
      "3) Try in Chrome Incognito tab\n" +
      "4) Try on a different network entirely";
  } else if (cors.body?.error) {
    const err = cors.body.error;
    const msg = (err.message || "").toLowerCase();
    if (msg.includes("api key") || msg.includes("referer") || msg.includes("referrer")) {
      diagnosis +=
        `API key restriction blocks this origin (HTTP ${cors.status}).\n` +
        `${err.status} – ${err.message}\n\n` +
        `Fix: GCP Console → project quickdukaan-3ec39 → APIs & Services → ` +
        `Credentials → Browser API key → Application restrictions → set to "None", OR ` +
        `add HTTP referrers:\n` +
        `• https://${window.location.hostname}/*\n` +
        `• https://*.preview.emergentagent.com/*\n` +
        `• https://quickdukaan.in/*`;
    } else {
      diagnosis +=
        `Identity Toolkit error (HTTP ${cors.status}):\n` +
        `${err.status} – ${err.message}`;
    }
  } else if (!cors.ok && cors.status !== 401 && cors.status !== 403) {
    // 401/403 here is expected (auth required for /v1/projects), so only flag others
    diagnosis +=
      `Identity Toolkit returned HTTP ${cors.status || "?"}. ` +
      `Likely: Identity Toolkit API not enabled on project ${firebaseConfig.projectId}.`;
  } else {
    diagnosis +=
      `All Firebase + reCAPTCHA endpoints reachable, API key accepted.\n\n` +
      `OTP still failing? Remaining causes:\n` +
      `1) App Check enforcement on Phone Auth without a debug token\n` +
      `2) Browser sending stale service-worker cached JS — fully close tab + open in Incognito\n` +
      `3) Mobile Chrome has cached an old reCAPTCHA token — clear site data for this domain\n` +
      `4) Phone number format wrong (must be E.164: +91XXXXXXXXXX)`;
  }

  return { reach, cors, diagnosis, version: DIAG_VERSION };
}



/**
 * Send an OTP via Firebase. Returns a confirmation result the caller must keep
 * around to verify the user-entered code via `confirmation.confirm(code)`.
 *
 * @param {string} phoneE164  e.g. "+919876543210"
 * @param {string} recaptchaContainerId  id of an invisible <div> in the DOM
 */
export async function sendOtp(phoneE164, recaptchaContainerId) {
  const verifier = getRecaptcha(recaptchaContainerId);
  try {
    return await signInWithPhoneNumber(auth, phoneE164, verifier);
  } catch (error) {
    // Surface the actual Firebase error code + message for debugging.
    console.error("Firebase OTP Error:", error);
    const firebaseMessage = error?.code
      ? `${error.code} - ${error.message}`
      : (error?.message || "Unknown Firebase error");
    // Attach a normalized message back onto the error so the UI can show it.
    if (error && !error._firebaseMessage) {
      try { error._firebaseMessage = firebaseMessage; } catch { /* ignore */ }
    }
    throw error;
  }
}

/**
 * Verify the entered code; on success returns a Firebase ID token suitable for
 * backend validation.
 */
export async function verifyOtp(confirmation, code) {
  const result = await confirmation.confirm(code);
  const idToken = await result.user.getIdToken(/* forceRefresh */ true);
  return { idToken, firebaseUser: result.user };
}

// Map Firebase auth error codes to friendly user-facing messages. We always
// include the raw `auth/...` code so debugging is straightforward.
export function describeFirebaseError(err) {
  const code = err?.code || "";
  const raw = err?.message || "";
  const friendly = (() => {
    switch (code) {
      case "auth/invalid-phone-number":     return "Invalid phone number. Please re-enter your 10-digit mobile.";
      case "auth/missing-phone-number":     return "Please enter your mobile number.";
      case "auth/quota-exceeded":           return "Daily SMS limit reached. Please try later.";
      case "auth/too-many-requests":        return "Too many attempts from this device. Please try after some time.";
      case "auth/invalid-verification-code":return "Incorrect OTP. Please re-check and try again.";
      case "auth/code-expired":             return "OTP expired. Tap Resend to receive a new one.";
      case "auth/network-request-failed":   return "Network request failed while contacting Firebase.";
      case "auth/operation-not-allowed":    return "Phone sign-in is disabled in Firebase Console.";
      case "auth/unauthorized-domain":      return "This domain is not in Firebase → Authentication → Settings → Authorized domains.";
      case "auth/captcha-check-failed":     return "reCAPTCHA verification failed. Reload and try again.";
      case "auth/invalid-app-credential":   return "Invalid reCAPTCHA / app credential. Verify Firebase Web API key.";
      case "auth/app-not-authorized":       return "This app is not authorized to use Firebase Auth. Check Firebase project + SHA settings.";
      default: return raw || "Something went wrong.";
    }
  })();
  // Suffix the raw error code in brackets so we can debug from the UI itself.
  return code ? `${friendly} [${code}]` : friendly;
}
